import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Calendar, User as UserIcon, Tag } from "lucide-react";

export default function SermonDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/sermons/${id}`);
        setS(data);
        const rel = await api.get("/sermons", { params: data.series ? { series: data.series } : {} });
        setRelated(rel.data.filter((r) => r.id !== id).slice(0, 4));
      } catch {}
    })();
  }, [id]);

  if (!s) return <div className="pt-32 ccn-container">Loading…</div>;

  return (
    <div data-testid="sermon-detail-page" className="pt-28 pb-20">
      <div className="ccn-container">
        <Link to="/sermons" data-testid="back-to-sermons" className="inline-flex items-center gap-2 text-[#575A58] hover:text-[#C8664D] mb-8">
          <ArrowLeft className="w-4 h-4" /> All sermons
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="rounded-2xl overflow-hidden bg-[#2A2E2C] aspect-video shadow-xl">
              <iframe
                data-testid="sermon-youtube-player"
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${s.youtube_id}?rel=0`}
                title={s.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            {s.series && <div className="mt-6 ccn-overline">{s.series}</div>}
            <h1 className="ccn-h2 mt-3" data-testid="sermon-title">{s.title}</h1>
            <div className="flex flex-wrap items-center gap-6 mt-5 text-[#575A58]">
              <span className="flex items-center gap-2"><UserIcon className="w-4 h-4" /> {s.speaker}</span>
              {s.date && (
                <span className="flex items-center gap-2"><Calendar className="w-4 h-4" />
                  {new Date(s.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              )}
            </div>
            {s.description && <p className="mt-8 text-lg text-[#1E1E1E] leading-relaxed">{s.description}</p>}
            {s.tags?.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-2">
                {s.tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#F2EBE5] text-[#575A58]">
                    <Tag className="w-3 h-3" />{t}
                  </span>
                ))}
              </div>
            )}
          </div>

          <aside className="space-y-5">
            <div className="ccn-overline">More from this series</div>
            {related.length === 0 && <div className="text-sm text-[#575A58]">No related sermons.</div>}
            {related.map((r) => (
              <Link key={r.id} to={`/sermons/${r.id}`} className="block ccn-card overflow-hidden" data-testid={`related-sermon-${r.id}`}>
                <div className="flex gap-4 p-3">
                  <img src={`https://img.youtube.com/vi/${r.youtube_id}/mqdefault.jpg`} alt="" className="w-28 h-20 object-cover rounded-lg" />
                  <div className="flex-1">
                    <div className="font-heading text-lg leading-snug text-[#1E1E1E] line-clamp-2">{r.title}</div>
                    <div className="text-xs text-[#575A58] mt-1">{r.speaker}</div>
                  </div>
                </div>
              </Link>
            ))}
          </aside>
        </div>
      </div>
    </div>
  );
}
